import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Divider,
  LinearProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import PixiWorldViewer from '../components/PixiWorldViewer';

const ClientList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addingAttachment, setAddingAttachment] = useState(false);
  const [generatingWorld, setGeneratingWorld] = useState(false);
  const [worldDialogOpen, setWorldDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [worldUrl, setWorldUrl] = useState('');
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: ''
  });
  const [newClientForm, setNewClientForm] = useState({
    email: ''
  });
  const [worldConfig, setWorldConfig] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const columns = [
    { 
      field: 'user_id', 
      headerName: 'ID', 
      flex: 0.5,
      minWidth: 60,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    { 
      field: 'user_email', 
      headerName: t('email'), 
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
            {params.value.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'total_bonus', 
      headerName: t('total_points'), 
      flex: 0.8,
      minWidth: 100,
      type: 'number',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="success"
          variant="filled"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    { 
      field: 'level_info', 
      headerName: t('level'), 
      flex: 0.8,
      minWidth: 120,
      valueGetter: (params) => params.row.level_info?.level_name || 'N/A',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 2.5,
      minWidth: 400,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('view_bonuses')}>
            <Button
              size="small"
              onClick={() => handleShowBonuses(params.row)}
              variant="contained"
              color="info"
              startIcon={<CardGiftcardIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: 2,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {t('bonuses')}
            </Button>
          </Tooltip>
          <Tooltip title={t('generate_world')}>
            <Button
              size="small"
              onClick={() => handleGenerateWorld(params.row)}
              variant="contained"
              color="success"
              startIcon={<VideogameAssetIcon />}
              disabled={generatingWorld}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: 2,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {t('world')}
            </Button>
          </Tooltip>
          <Tooltip title={t('add_attachment')}>
            <Button
              size="small"
              onClick={() => handleAddAttachment(params.row)}
              variant="contained"
              color="secondary"
              disabled={addingAttachment}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: 2,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {t('add')}
            </Button>
          </Tooltip>
          <Tooltip title={t('delete_user')}>
            <Button
              size="small"
              onClick={() => handleDeleteUser(params.row)}
              variant="contained"
              color="error"
              disabled={deletingUser === params.row.user_id}
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: 2,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {t('delete')}
            </Button>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get('/users/get-all-users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(t('failed_to_load_clients'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users/get-all-users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('failed_to_load_users'));
    }
  };

  const handleShowBonuses = (client) => {
    navigate(`/client/bonuses?email=${encodeURIComponent(client.user_email)}`);
  };

  const handleGenerateWorld = async (client) => {
    try {
      setGeneratingWorld(true);
      setSelectedClient(client);
      setWorldConfig(null);
      setError('');
      
      const response = await axios.post(`/pixi-world/world/${client.user_id}`, {}, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setWorldConfig(response.data);
      setWorldDialogOpen(true);
    } catch (err) {
      console.error('Error generating world:', err);
      setError(t('failed_to_generate_world'));
      setWorldDialogOpen(false);
    } finally {
      setGeneratingWorld(false);
    }
  };

  const handleCloseWorldDialog = () => {
    setWorldDialogOpen(false);
    setWorldConfig(null);
    setSelectedClient(null);
  };

  const handleAddAttachment = (client) => {
    setSelectedClient(client);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedClient(null);
    setFormData({ email: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;

    try {
      setAddingAttachment(true);
      setError('');
      setSuccess('');

      const response = await axios.post(
        '/attachments/add',
        {
          user_email: selectedClient.user_email
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(t('attachment_added_successfully'));
      fetchClients(); // Refresh the list
      handleCloseDialog();
    } catch (err) {
      console.error('Error adding attachment:', err);
      setError(t('failed_to_add_attachment'));
    } finally {
      setAddingAttachment(false);
    }
  };

  const handleNewClient = () => {
    setNewClientDialogOpen(true);
  };

  const handleCloseNewClientDialog = () => {
    setNewClientDialogOpen(false);
    setNewClientForm({ email: '' });
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const response = await axios.post(
        '/users/register',
        { email: newClientForm.email },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setSuccess(t('user_registered_successfully'));
      fetchClients(); // Refresh the list
      handleCloseNewClientDialog();
    } catch (err) {
      console.error('Error registering user:', err);
      setError(err.response?.data?.detail || t('failed_to_register_user'));
    }
  };

  const handleDeleteUser = (client) => {
    setUserToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeletingUser(userToDelete.user_id);
      setError('');
      setSuccess('');

      const response = await axios.delete(`/users/${encodeURIComponent(userToDelete.user_email)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccess(t('user_deleted_successfully'));
      fetchClients(); // Refresh the list
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.detail || t('failed_to_delete_user'));
    } finally {
      setDeletingUser(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <PeopleIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('user_management')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('manage_users_bonuses_worlds')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <PeopleIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {clients.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_users')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <CardGiftcardIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {clients.reduce((sum, client) => sum + client.total_bonus, 0)}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_points')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('user_list')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('view_manage_registered_users')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleNewClient}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('add_new_user')}
            </Button>
          </Box>
          
          <Paper sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <DataGrid
              rows={clients}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              checkboxSelection
              disableSelectionOnClick
              loading={loading}
              getRowId={(row) => row.user_id}
              autoWidth
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-columnHeader': {
                  flex: 1,
                },
                '& .MuiDataGrid-cell': {
                  flex: 1,
                },
              }}
            />
          </Paper>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('add_attachment')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              {t('add_attachment_for_user')} <strong>{selectedClient?.user_email}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
            disabled={addingAttachment}
          >
            {addingAttachment ? <CircularProgress size={24} /> : t('add_attachment')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={worldDialogOpen} 
        onClose={handleCloseWorldDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('game_world_for')} {selectedClient?.user_email}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {worldConfig ? (
            <Box sx={{ height: '700px' }}>
              <PixiWorldViewer 
                worldConfig={worldConfig} 
                onRefresh={() => handleGenerateWorld(selectedClient)}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={60} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseWorldDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={newClientDialogOpen} 
        onClose={handleCloseNewClientDialog}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('add_new_user')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 2, minWidth: '400px' }}>
            <TextField
              label={t('email_address')}
              type="email"
              value={newClientForm.email}
              onChange={(e) => setNewClientForm({ email: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              helperText={t('enter_email_for_new_user')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseNewClientDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleCreateClient} 
            variant="contained"
            disabled={!newClientForm.email}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {t('create_user')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title" sx={{ 
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ mr: 2 }} />
            {t('confirm_user_deletion')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {userToDelete && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {t('warning_action_cannot_be_undone')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('about_to_delete_user_data')}
              </Typography>
              <Card sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: 'primary.main' }}>
                    {userToDelete.user_email.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {userToDelete.user_email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('user_id')} {userToDelete.user_id}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${userToDelete.total_bonus} ${t('points')}`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  <Chip 
                    label={`${t('level')} ${userToDelete.level_info?.level_name || 'N/A'}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Card>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                {t('this_will_permanently_delete')}
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>{t('user_account_and_profile')}</li>
                <li>{t('all_user_bonuses_and_points')}</li>
                <li>{t('users_game_world_and_planets')}</li>
                <li>{t('all_attachment_positions')}</li>
                <li>{t('all_associated_data')}</li>
              </ul>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                {t('please_type_delete_to_confirm')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deletingUser === userToDelete?.user_id}
            startIcon={deletingUser === userToDelete?.user_id ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff5252 0%, #d84315 100%)',
              }
            }}
          >
            {deletingUser === userToDelete?.user_id ? t('deleting') : t('delete_user_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientList; 